import { useContext, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useResizeObserver } from "@reactuses/core";
import { useControlledState } from "@react-stately/utils";
import {
  ComboBox,
  ComboBoxStateContext,
  ComboBoxValue,
  Collection,
  FieldError,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  ProgressBar,
  Tag as RACTag,
  TagGroup,
  TagList,
  Text,
  useFilter,
  type ComboBoxProps,
  type ComboBoxValueRenderProps,
  type Key,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { formField, type FormFieldVariant } from "../../../../styled-system/recipes";
import { popoverStyles, tagChipStyles } from "../styles/styles";
import { defaultTagColor } from "../../core/basic/Tag";
import { Button } from "../../core/basic/Button";
import { ImSpinner2 } from "react-icons/im";
import { FaPlus, FaX } from "react-icons/fa6";

interface MultiSelectProps<T extends object> extends Omit<ComboBoxProps<T, "multiple">, "children" | "className"> {
  /** Custom item renderer for dropdown. Default assumes { label, value, color? }. */
  children?: (item: T) => ReactNode;
  /** Formily workaround — same as children but as a separate prop */
  itemRenderer?: (item: T) => ReactNode;
  /** Custom tag renderer for selected items in the input */
  tagRenderer?: (item: T) => ReactNode;

  label?: ReactNode;
  description?: string;
  /** Error message — also marks the field as invalid */
  errorMessage?: string;
  placeholder?: string;
  startIcon?: ReactNode;

  variant?: FormFieldVariant["variant"];
  css?: SystemStyleObject;

  onCreate?: (inputValue: string) => void;
  createLabel?: ReactNode | ((inputValue: string) => ReactNode);
  isLoading?: boolean;
  listBoxFooter?: ReactNode;
  emptyMessage?: ReactNode;
}

type DefaultItem = {
  label: string;
  value: string | number;
  color?: string;
  isDisabled?: boolean;
};

const CREATE_ITEM_ID = "__multiselect_create__";

const listBoxStyles = css({
  maxHeight: "240px",
  paddingY: "xsmall",
  scrollbarColor: "{colors.lightgrey} transparent",
  scrollbarGutter: "stable both-edges",
  scrollbarWidth: "thin",
  overflowY: "auto",
  scrollPaddingBottom: "48px",
  "& [data-hovered]:not([data-selected]):not([data-focused])": {
    color: "darkblue",
    backgroundColor: "black/10",
  },
});

const multiSelectItemClassName = css({
  cursor: "pointer",
  paddingY: "xsmall",
  paddingX: "medium",
  color: "darkblue/80",
  fontSize: "small",
  backgroundColor: "white",
  _disabled: {
    cursor: "not-allowed",
    color: "text.secondary",
    backgroundColor: "white",
  },
  _selected: {
    display: "none",
  },
  _focusVisible: { outline: "none" },
  "&[data-focused]": {
    color: "darkblue",
    backgroundColor: "black/10",
  },
});

const createActionClassName = css({
  position: "sticky",
  bottom: 0,
  zIndex: 1,
  borderTop: "1px solid {colors.lightgrey}",
  textAlign: "left",
  width: "100%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "small",
  paddingY: "small",
  paddingX: "medium",
  fontSize: "small",
  fontWeight: 600,
  color: "darkblue",
  backgroundColor: "white",
  transition: "background-color 0.15s ease",
  _focusVisible: { outline: "none" },
  "&[data-hovered], &[data-focused], &[data-pressed]": {
    backgroundImage: "linear-gradient({colors.black/5}, {colors.black/5})",
  },
  "&[data-hovered] .createBadge, &[data-focused] .createBadge, &[data-pressed] .createBadge": {
    transform: "scale(1.06)",
  },
});

const createActionIconClassName = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: "20px",
  height: "20px",
  borderRadius: "light",
  color: "white",
  backgroundColor: "fuchsia",
  fontSize: "10px",
  transition: "transform 0.15s ease",
});

const emptyStateStyles = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingY: "small",
  paddingX: "medium",
  fontSize: "small",
  color: "text.secondary",
});

const tagListStyles = css({
  display: "flex",
  gap: "xsmall",
  flexWrap: "wrap",
  maxHeight: "110px",
  overflowY: "auto",
});

const loadingSpinnerStyles = css({
  display: "flex",
  alignItems: "center",
  color: "text.secondary",
});

const spinnerAnimation = css({
  animation: "spin",
});

export function MultiSelect<T extends object>({
  items,
  children,
  itemRenderer,
  tagRenderer,
  label,
  description,
  errorMessage,
  placeholder,
  startIcon,
  variant,
  css: cssProp,
  onCreate,
  createLabel,
  isLoading,
  listBoxFooter,
  emptyMessage,
  allowsEmptyCollection = true,
  menuTrigger = "focus",
  defaultFilter,
  isInvalid,
  inputValue,
  defaultInputValue,
  onInputChange,
  value,
  defaultValue,
  onChange,
  disabledKeys: userDisabledKeys,
  ...rest
}: MultiSelectProps<T>) {
  const { t } = useTranslation(["common"]);
  const classes = formField({ variant });
  const { contains } = useFilter({ sensitivity: "base" });
  const resolvedCreateLabel = createLabel ?? t(($) => $.common["Create new..."]);
  const resolvedEmptyMessage = emptyMessage ?? t(($) => $.common["No results"]);

  const groupRef = useRef<HTMLDivElement>(null);
  const [groupWidth, setGroupWidth] = useState<string>();

  const [currentInputValue, setCurrentInputValue] = useControlledState(
    inputValue,
    defaultInputValue ?? "",
    onInputChange,
  );

  const [currentValue, setCurrentValue] = useControlledState<readonly Key[], Key[]>(
    value,
    defaultValue ?? [],
    onChange,
  );

  useResizeObserver(groupRef, () => {
    if (groupRef.current) setGroupWidth(`${groupRef.current.offsetWidth}px`);
  });

  const resolvedChildren = children ?? itemRenderer ?? defaultRenderer;

  const handleChange = (next: Key[]) => {
    if (next.includes(CREATE_ITEM_ID)) {
      onCreate?.(currentInputValue);
      return;
    }
    setCurrentValue(next);
  };

  const disabledKeys = useMemo(
    () => [...(userDisabledKeys ?? []), ...currentValue.filter((k) => k !== CREATE_ITEM_ID)],
    [userDisabledKeys, currentValue],
  );

  const baseFilter = defaultFilter ?? contains;
  const filter =
    onCreate ?
      (textValue: string, inputValue: string) => textValue === CREATE_ITEM_ID || baseFilter(textValue, inputValue)
    : baseFilter;

  return (
    <ComboBox
      selectionMode="multiple"
      allowsEmptyCollection={allowsEmptyCollection}
      menuTrigger={menuTrigger}
      defaultFilter={filter}
      isInvalid={isInvalid ?? !!errorMessage}
      inputValue={currentInputValue}
      onInputChange={setCurrentInputValue}
      value={currentValue}
      onChange={handleChange}
      disabledKeys={disabledKeys}
      className={cx(classes.fieldWrapper, css(cssProp))}
      {...rest}
    >
      {label && <Label className={classes.label}>{label}</Label>}
      <Group
        ref={groupRef}
        className={cx(
          classes.inputBox,
          css({
            flexWrap: "wrap",
            gap: "xsmall",
            paddingY: "xsmall",
            borderRadius: "card",
          }),
        )}
      >
        {startIcon && <span className={css({ fontSize: "medium", fontWeight: "300" })}>{startIcon}</span>}
        <ComboBoxValue<T>>
          {(renderProps) => <SelectedTags {...renderProps} renderTag={tagRenderer ?? defaultTagRenderer} />}
        </ComboBoxValue>
        <MultiSelectInput
          inputClassName={cx(classes.input, css({ minWidth: "80px" }))}
          placeholder={placeholder}
          currentInputValue={currentInputValue}
          onCreate={onCreate}
        />
        {isLoading && (
          <ProgressBar aria-label={t(($) => $.common["Loading..."])} isIndeterminate className={loadingSpinnerStyles}>
            <ImSpinner2 className={spinnerAnimation} />
          </ProgressBar>
        )}
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
      <Popover
        triggerRef={groupRef}
        placement="bottom"
        offset={4}
        style={{ "--combobox-group-width": groupWidth } as CSSProperties}
        className={cx(popoverStyles, css({ width: "var(--combobox-group-width)", overflow: "hidden" }))}
      >
        <PopoverBody<T>
          items={items}
          resolvedChildren={resolvedChildren}
          listBoxFooter={listBoxFooter}
          isLoading={isLoading}
          emptyMessage={resolvedEmptyMessage}
          onCreate={onCreate}
          createLabel={resolvedCreateLabel}
          currentInputValue={currentInputValue}
        />
      </Popover>
    </ComboBox>
  );

  function defaultRenderer(item: object) {
    if (
      "label" in item &&
      "value" in item &&
      (typeof item.value === "string" || typeof item.value === "number") &&
      typeof item.label === "string"
    ) {
      const { label, value, isDisabled, color } = item as DefaultItem;
      return (
        <ListBoxItem id={value} textValue={label} isDisabled={isDisabled} className={multiSelectItemClassName}>
          <span className={css({ display: "flex", alignItems: "center", gap: "small" })}>
            {color && (
              <span
                className={css({ width: "8px", height: "8px", borderRadius: "full", flexShrink: 0 })}
                style={{ backgroundColor: color }}
              />
            )}
            <span>{label || "%missing_label%"}</span>
          </span>
        </ListBoxItem>
      );
    }
    throw new Error("You need a custom renderer");
  }

  function defaultTagRenderer(item: object) {
    if ("label" in item && "value" in item && typeof item.label === "string") {
      const { label, value, color } = item as DefaultItem;
      return (
        <RACTag
          id={value}
          textValue={label}
          className={tagChipStyles}
          style={{ "--tag-color": color ?? defaultTagColor } as CSSProperties}
        >
          {label}
          <Button variant="contentOnly" slot="remove">
            <FaX className={css({ fontSize: "xxsmall", fontWeight: "bold" })} />
          </Button>
        </RACTag>
      );
    }
    throw new Error("You need a custom tagRenderer");
  }
}

function MultiSelectInput({
  inputClassName,
  placeholder,
  currentInputValue,
  onCreate,
}: {
  inputClassName: string;
  placeholder?: string;
  currentInputValue: string;
  onCreate?: (inputValue: string) => void;
}) {
  const state = useContext(ComboBoxStateContext);
  return (
    <Input
      className={inputClassName}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (
          e.key === "Enter" &&
          onCreate &&
          currentInputValue.trim() !== "" &&
          !e.currentTarget.getAttribute("aria-activedescendant")
        ) {
          e.preventDefault();
          onCreate(currentInputValue);
          state?.setInputValue("");
          state?.close();
        }
      }}
    />
  );
}

function PopoverBody<T extends object>({
  items,
  resolvedChildren,
  listBoxFooter,
  isLoading,
  emptyMessage,
  onCreate,
  createLabel,
  currentInputValue,
}: {
  items: Iterable<T> | undefined;
  resolvedChildren: (item: T) => ReactNode;
  listBoxFooter?: ReactNode;
  isLoading?: boolean;
  emptyMessage: ReactNode;
  onCreate?: (inputValue: string) => void;
  createLabel: ReactNode | ((inputValue: string) => ReactNode);
  currentInputValue: string;
}) {
  const { t } = useTranslation(["common"]);
  const state = useContext(ComboBoxStateContext);
  const dynamicCount = state ? state.collection.size - (onCreate ? 1 : 0) : 0;
  const showInlineEmptyState = !!state && !!onCreate && dynamicCount === 0;

  return (
    <>
      {showInlineEmptyState && (
        <div className={emptyStateStyles}>
          {isLoading ?
            <ProgressBar aria-label={t(($) => $.common["Loading..."])} isIndeterminate className={loadingSpinnerStyles}>
              <ImSpinner2 className={spinnerAnimation} />
            </ProgressBar>
          : emptyMessage}
        </div>
      )}
      <ListBox
        className={listBoxStyles}
        renderEmptyState={() => (
          <div className={emptyStateStyles}>
            {isLoading ?
              <ProgressBar
                aria-label={t(($) => $.common["Loading..."])}
                isIndeterminate
                className={loadingSpinnerStyles}
              >
                <ImSpinner2 className={spinnerAnimation} />
              </ProgressBar>
            : emptyMessage}
          </div>
        )}
      >
        <Collection items={items}>{resolvedChildren}</Collection>
        {listBoxFooter}
        {onCreate && (
          <ListBoxItem
            id={CREATE_ITEM_ID}
            textValue={CREATE_ITEM_ID}
            aria-label={t(($) => $.common["Create new"])}
            className={createActionClassName}
            onAction={() => {
              onCreate(currentInputValue);
              state?.setInputValue("");
              state?.close();
            }}
          >
            <span className={cx(createActionIconClassName, "createBadge")} aria-hidden>
              <FaPlus />
            </span>
            <span>{typeof createLabel === "function" ? createLabel(currentInputValue) : createLabel}</span>
          </ListBoxItem>
        )}
      </ListBox>
    </>
  );
}

function SelectedTags<T extends object>({
  selectedItems,
  state,
  renderTag,
}: ComboBoxValueRenderProps<T> & { renderTag: (item: T) => ReactNode }) {
  const { t } = useTranslation(["common"]);
  const items = useMemo(() => selectedItems.filter((item): item is T => item != null), [selectedItems]);
  return (
    <TagGroup
      aria-label={t(($) => $.common["Selected items"])}
      onRemove={(keys) => {
        if (Array.isArray(state.value)) {
          const remainingKeys = (state.value as readonly Key[]).filter((k) => !keys.has(k));
          state.setValue(remainingKeys);
        }
      }}
    >
      <TagList items={items} className={tagListStyles}>
        {renderTag}
      </TagList>
    </TagGroup>
  );
}
